import { defineComponent } from 'vue';
import utils from '../../Shared/utils.jsx';
import BaseComponent from './BaseComponentMixin.jsx';
import TranslationContainer from './TranslationContainer.jsx';
import DocumentTreePanel from './DocumentTreePanel.jsx';

const EMPTY_OUTER_STYLE = Object.freeze({});

export default defineComponent({
    name: 'tree-view',
    mixins: [BaseComponent],
    components: {
        TranslationContainer,
        DocumentTreePanel,
    },
    data: () => ({
        title: null,
        sourceurl: null,
        sourceraw: null,
        cmodel: null,
        selectednode: null,
        sourceurl_literal: null,
        history: [],
        resizable: false,
        tree_width: 400,
    }),
    async created() {
        if (this.controlData.SourceType === 'Url' && this.controlData.SourceUrl) {
            this.sourceurl = utils.compile(this, this.controlData.SourceUrl);

            this.$watch(
                function () {
                    return utils.evaluate(this.sourceurl, this);
                },
                function (val, oldval) {
                    console.log(`TreeView watch fired on this.sourceurl: ${val}`);
                    this.Refresh();
                }
            );
        }
        else if (this.controlData.SourceType === 'Raw' && this.controlData.SourceRaw)
            this.sourceraw = utils.compileObject(this, this.controlData.SourceRaw);

        this.title = utils.compile(this, this.controlData.Title);

        this.resizable = (
            this.controlData.WidthResizeBarVisible &&
            this.controlData.SizeOptions &&
            this.controlData.SizeOptions.Width &&
            this.controlData.SizeOptions.Width.Mode == 'Fixed' &&
            this.controlData.SizeOptions.Width.Units == 'Pixels' &&
            this.controlData.SizeOptions.Width.Value) ? true : false;

        if (this.resizable)
            this.tree_width = parseInt(this.controlData.SizeOptions.Width.Value);
    },
    computed: {
        Model: function () {
            return this.cmodel;
        },
        SelectedNode: function () {
            return this.selectednode;
        },
        treeTitle() {
            try {
                return utils.evaluate(this.title, this);
            }
            catch (e) {
                return '';
            }
        },
        panelOuterStyle() {
            if (this.resizable)
                return EMPTY_OUTER_STYLE;
            return utils.getSize(this.controlData.SizeOptions, this.parentType, this.$parent);
        },
    },
    methods: {
        async preRenderComplete() {
            await this.Refresh();
            this.finishRenderHandler(this);
        },
        ExpandToSelected() {
            this.$refs.treePanel?.expandToNode(this.selectednode);
        },
        ExpandToNode(node) {
            return this.$refs.treePanel?.expandToNode(node);
        },
        SelectNode(node, expandto) {
            if (!expandto && node === this.selectednode)
                return null;

            let path;
            this.selectednode = node;
            if (this.$refs.treePanel)
                path = this.$refs.treePanel.setSelectedNode(node, expandto);
            else if (expandto)
                path = this.ExpandToNode(node);

            utils.executeAndCompileAllActions(this.controlData.Actions, null, this);

            return path;
        },
        onPanelSelectedNode(node) {
            if (node === this.selectednode)
                return;
            this.selectednode = node;
            utils.executeAndCompileAllActions(this.controlData.Actions, null, this);
        },
        SetModel(cmodel) {
            this.$refs.treePanel?.clearExpansionState();
            this.cmodel = cmodel;
        },
        async SetSourceUrl(value) {
            if (this.sourceurl_literal) {
                this.history.push(this.sourceurl_literal);
                this.sourceurl_literal = value;
            }
            this.$refs.treePanel?.clearExpansionState();
            this.cmodel = null;
            this.cmodel = await utils.api.get(value);
        },
        async Refresh() {
            this.$refs.treePanel?.clearExpansionState();
            if (this.controlData.SourceType == 'Url' && this.sourceurl) {
                this.cmodel = null;
                this.sourceurl_literal = utils.evaluate(this.sourceurl, this);
                this.cmodel = await utils.api.get(this.sourceurl_literal);
            }
            else if (this.sourceraw)
                this.cmodel = utils.evaluateObject(this.sourceraw, this);
            else
                this.cmodel = {};
        },
        async navigateBack() {
            if (this.history.length > 0) {
                const last = this.history[this.history.length - 1];
                this.history.splice(this.history.length - 1);

                this.$refs.treePanel?.clearExpansionState();
                this.cmodel = null;
                this.cmodel = await utils.api.get(last);
            }
        },
        clearExpansionState() {
            this.$refs.treePanel?.clearExpansionState();
        },
        splitterMouseDown(e) {
            document.addEventListener('mousemove', this.splitterMouseMove);
            document.addEventListener('mouseup', this.splitterMouseUp);
            this.initialX = e.clientX;
            this.initialWidth = this.tree_width;
            e.cancelBubble = true;
            e.stopPropagation();
        },
        splitterMouseMove(e) {
            const dx = e.clientX - this.initialX;
            this.tree_width = this.initialWidth + dx;
            e.cancelBubble = true;
            e.stopPropagation();
        },
        splitterMouseUp(e) {
            document.removeEventListener('mousemove', this.splitterMouseMove);
            document.removeEventListener('mouseup', this.splitterMouseUp);
            e.cancelBubble = true;
            e.stopPropagation();
        },
    },
    props: {
    },
    render() {
        if (!this.todisplay)
            return null;

        if (!this.cmodel)
            return <div><TranslationContainer context={this} value={"Loading..."} /></div>;

        const panel = (
            <DocumentTreePanel
                ref="treePanel"
                class={`c-TreeView c-name-${this.name || 'unnamed'}`}
                model={this.cmodel}
                title={this.treeTitle}
                width={this.resizable ? this.tree_width : null}
                history={this.history}
                treeName={this.controlData.Name || 'Tree'}
                root={this.root}
                outerStyle={this.resizable ? null : this.panelOuterStyle}
                onUpdate:selectedNode={(node) => this.onPanelSelectedNode(node)}
                onNavigateBack={() => this.navigateBack()}
            />
        );

        if (!this.resizable)
            return panel;

        return (
            <div
                class={`c-TreeView c-name-${this.name || 'unnamed'}`}
                style={{
                    display: "flex",
                    flexDirection: "row",
                    flexGrow: "0",
                    flexShrink: "1",
                    alignSelf: "normal",
                }}>
                {panel}
                <div
                    style={{
                        backgroundColor: "gray", width: "5px", minWidth: "5px", maxWidth: "5px", overflow: "hidden", cursor: "ew-resize"
                    }}
                    onMousedown={(e) => this.splitterMouseDown(e)}>
                </div>
            </div>
        );
    }
});
